import { useDropzone, FileRejection, DropEvent } from 'react-dropzone';
import { toast } from 'react-toastify';

export const MAX_FILE_SIZE = 1024 * 1024 * 1024;

interface ImagePreview {
  file: File;
  preview: string;
}

interface UseMediaDropProps {
  onImagesChange: (images: ImagePreview[]) => void;
  maxFileSize?: number;
}

export const useMediaDrop = ({
  onImagesChange,
  maxFileSize = MAX_FILE_SIZE
}: UseMediaDropProps) => {
  const onDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    event: DropEvent
  ) => {
    if (fileRejections?.length > 0) {
      fileRejections?.forEach(rejection => {
        rejection?.errors?.forEach(err => {
          if (err?.code === 'file-too-large') {
            toast.error(
              `"${rejection?.file?.name}" exceeds the 1GB size limit.`
            );
          } else if (err?.code === 'file-invalid-type') {
            toast.error(`"${rejection?.file?.name}" is not a valid file type.`);
          }
        });
      });
    }

    if (acceptedFiles?.length > 0) {
      const validFiles = acceptedFiles?.filter(
        file => file?.size <= maxFileSize
      );
      const previews = validFiles?.map(file => ({
        file,
        preview: URL.createObjectURL(file)
      }));
      onImagesChange(previews);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpeg', '.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/x-msvideo': ['.avi'],
      'video/x-ms-wmv': ['.wmv'],
      'video/x-flv': ['.flv'],
      'video/x-matroska': ['.mkv'],
      'video/webm': ['.webm'],
      'video/ogg': ['.ogg']
    },
    maxSize: maxFileSize,
    multiple: true
  });

  return {
    getRootProps,
    getInputProps,
    isDragActive
  };
};
